import {
  Search,
  Filter,
  HotelSearchCriteria,
  Suggest,
  InitRequest,
  FetchRequest,
  User,
  Configurator,
} from 'types';
import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { HotelService, NetworkHelper } from 'api';
import { finalize, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { mapError, readQuery } from '../../utils';
import i18n from '../../plugins/i18n';
import { SSEHotels } from 'api';
import mapboxGl from 'mapbox-gl';
import { parseIdToCoordinates } from './searchCriteria';
import { Events } from './../../analytics/events';
import { HotelModuleTest } from '@/types/models/configurator/hotel';
import { ExpirationCountdown } from '@/plugins/expiration-countdown';

let initSubscription: Subscription = null;
let sseSubscription: Subscription = null;
let fetchSubscription: Subscription = null;

const FILTERS_SECTION_TITLES = {
  options: () => String(i18n.global.t('filters.sections.options')),
  facilities: () => String(i18n.global.t('filters.sections.amenities')),
  regions: () => String(i18n.global.t('filters.sections.neighborhood')),
  themes: () => String(i18n.global.t('filters.sections.themes')),
  brands: () => String(i18n.global.t('filters.sections.brands')),
  types: () => String(i18n.global.t('filters.sections.types')),
  rewardPrograms: () =>
    String(i18n.global.t('filters.sections.rewardPrograms')),
};

interface PageMeta {
  page: number; // base = 0
  from: number;
  total?: number;
  totalProcessed?: number;
}

export class State {
  filterIsOpened: boolean = false;
  refineSearchQuery = '';

  focusedHotel: Search.FocusInterface = { id: -1, isMarker: false };
  selectedHotelId: number = -1;

  searchState: Search.SearchState = new Search.SearchState();

  currentMapBounds: mapboxGl.LngLatBounds = null;
  primaryMapBounds: mapboxGl.LngLatBounds = null;
  showRedo: boolean = false;

  roomId: string = null;
  searchFinishedInCurrentBounds: boolean = false;

  pageMeta: PageMeta = { page: 0, from: 0 };
  hotels: Search.HotelDetails[] = [];
  filters = Filter.HotelCollectionFilterModel.composeBase();

  categorySelectionModal: {
    isShown: boolean;
    prices: Partial<Record<Filter.StarsType, number>>;
  } = {
    isShown: false,
    prices: {
      [Filter.StarsType.Three]: null,
      [Filter.StarsType.Four]: null,
      [Filter.StarsType.Five]: null,
    },
  };

  // expiration
  searchExpirationAt: Date = null;
  isSearchExpired: boolean = false;
}

const getters = <GetterTree<State, any>>{
  getTotalCount(state: State): string {
    return i18n.global.t(`searchpage.hotelsLength`, {
      total: state.pageMeta.total,
    });
  },
  getPageCounter(state: State): string {
    let to = state.pageMeta.page * Search.limitPerPage + Search.limitPerPage;
    if (to > state.pageMeta.totalProcessed) {
      to = state.pageMeta.totalProcessed;
    }

    const text = i18n.global.t(`searchpage.bottomText`, {
      from: state.pageMeta.from + 1,
      to,
      hotelsLength: state.pageMeta.totalProcessed,
    });

    return text;
  },

  getSorting(state: State) {
    return state.filters.sorting;
  },

  getFilters(state: State) {
    return state.filters;
  },

  getFacilitiesFilter(state: State) {
    return state.filters.facilities;
  },

  getSelectableFiltersTitled(state: State) {
    const facilities = state.filters.facilities;
    const options = state.filters.options;
    const hotelTypes = state.filters.hotelTypes;
    const rewardProgram = state.filters.rewardProgram;

    const xs: {
      title: string;
      key: Filter.SelectableKeys;
      items:
        | Filter.Option[]
        | Filter.Facility[]
        | Filter.HotelType[]
        | Filter.RewardProgram[];
    }[] = [];

    xs.push({
      key: Filter.SelectableKeys.Options,
      title: FILTERS_SECTION_TITLES.options(),
      items: options,
    });
    xs.push({
      key: Filter.SelectableKeys.Facilities,
      title: FILTERS_SECTION_TITLES.facilities(),
      items: facilities,
    });

    xs.push({
      key: Filter.SelectableKeys.HotelTypes,
      title: FILTERS_SECTION_TITLES.types(),
      items: hotelTypes,
    });
    xs.push({
      key: Filter.SelectableKeys.RewardProgram,
      title: FILTERS_SECTION_TITLES.rewardPrograms(),
      items: rewardProgram,
    });
    return xs;
  },

  isNoResultsOnMapAsFilter(state: State): boolean {
    return state.filters.useMap() && state.hotels.length === 0;
  },

  getSelectedOptionFilters(state: State): string[] {
    const options = state.filters.options.filterSelected();
    return options
      ? options.slice().map(it => (it as Filter.Option).id.toString())
      : [];
  },

  getPriceRange(state: State) {
    return state.filters.priceRange;
  },
  getStarsFilter(state: State) {
    return state.filters.stars;
  },
  isDefaultStarsState(state: State): boolean {
    return (
      state.filters.stars.every(it => it.selected) ||
      state.filters.stars.every(it => !it.selected)
    );
  },
  getRatingFilter(state: State) {
    return state.filters.rating;
  },
  selectedRatingFilter(state: State): Filter.Rating {
    return state.filters.rating.find(it => it.selected);
  },

  filtersOnDefault(state: State): boolean {
    return state.filters.isDefault();
  },

  getHotels(state: State): Search.HotelDetails[] {
    // if (state.searchState.isFailed()) return [];

    if (!state.hotels) return [];

    const byQuery = state.hotels
      .slice()
      .filter(it =>
        it.name
          .toLowerCase()
          .includes(state.refineSearchQuery.toLocaleLowerCase()),
      );

    return byQuery;
  },

  isFiltersOpen(state: State) {
    return state.filterIsOpened;
  },

  getOriginHotelsCount(state: State) {
    return state.hotels.length;
  },

  getProgressLoaderState(state: State) {
    return state.searchState.initLoading || state.searchState.fetchLoading;
  },

  getBestHotelsCount(state: State) {
    // if (state.searchState.isFailed()) {
    //   return 0;
    // }

    if (!state.hotels) {
      return 0;
    }

    if (
      state.refineSearchQuery.length > 0 &&
      state.searchState.suggestIsFinished()
    ) {
      return state.hotels.length;
    } else {
      return state.hotels.length;
    }
  },

  getAllHotelsCount(state: State) {
    return state.hotels.length;
  },

  isPriceTokenEnable(state: State) {
    return false;
  },

  getPage(state: State) {
    return state.pageMeta.page;
  },

  paginationIsEnable(state: State, getters: any) {
    return (
      !state.searchState.fetchLoading &&
      state.hotels &&
      state.pageMeta.totalProcessed > Search.limitPerPage &&
      state.refineSearchQuery.length === 0
    );
  },

  getHotelsListAmount(state: State) {
    return state.hotels ? state.hotels.length : 0;
  },
};

const mutations = <MutationTree<State>>{
  setSearchQuery(state: State, query: string) {
    state.refineSearchQuery = query;

    if (query === '') {
      state.searchState.suggestState = Search.SearchStateTypes.IDLE;
    }
  },

  setLoading(state: State, loading: boolean) {
    state.searchState.initLoading = loading;
  },

  setFetchLoading(state: State, loading: boolean) {
    state.searchState.fetchLoading = loading;
  },

  focus(state: State, model: Search.FocusInterface) {
    const id = model.focus ? model.id : -1;
    state.focusedHotel = { id, isMarker: model.focus && model.isMarker };
  },

  selectHotel(state: State, hotelId: number) {
    state.selectedHotelId = hotelId;
  },

  reset(state: State) {
    state.searchState.reset();

    state.showRedo = false;
    state.hotels = [];
    state.primaryMapBounds = null;
    state.refineSearchQuery = '';
    state.focusedHotel = { id: -1, isMarker: false };
    state.currentMapBounds = null;
    state.searchFinishedInCurrentBounds = false;

    state.roomId = null;

    state.pageMeta = { page: 0, from: 0 };

    if (initSubscription) initSubscription.unsubscribe();
    if (sseSubscription) sseSubscription.unsubscribe();
    if (fetchSubscription) fetchSubscription.unsubscribe();

    state.searchExpirationAt = null;
    state.isSearchExpired = false;
    ExpirationCountdown.stop();
  },

  showFilter(state: State, value: boolean) {
    state.filterIsOpened = state.refineSearchQuery.length > 0 ? false : value;
  },

  setHotels(state: State, res: Search.HotelDetails[]) {
    // enrich hotels prices with currency
    const currency = NetworkHelper.getCurrency().currency;
    res.forEach(hotel => {
      hotel.price.currency = currency;
    });

    state.hotels = res;
  },

  setFilters(state: State, filter: Filter.HotelCollectionFilterModel) {
    state.filters = filter;

    const hotelClass = filter.stars.filter(it => it.selected);
    if (hotelClass.length > 0) {
      Search.HotelCategorySurvey.save(hotelClass.map(it => it.type));
    } else {
      Search.HotelCategorySurvey.save([Filter.StarsType.Any]);
    }
  },

  setMapBounds(state: State, bounds: mapboxGl.LngLatBounds) {
    state.currentMapBounds = bounds;
    state.searchFinishedInCurrentBounds = false;
  },

  updateSearchState(state: State, payload: Search.ISearchState) {
    state.searchState.error = payload.error
      ? mapError(payload.error)
      : undefined;

    if (payload.state === Search.SearchStateTypes.STARTED) {
      state.searchState.mapLoading = payload.mapLoading ?? false;
      state.searchState.initLoading = payload.initLoading ?? true;
      state.searchState.fetchLoading = payload.fetchLoading ?? true;

      state.searchState.searchStartTime = new Date().getTime();
      state.searchState.searchFinishedTime = 0;

      state.searchState.state = payload.state;
    } else if (
      payload.state === Search.SearchStateTypes.FAILED ||
      payload.state === Search.SearchStateTypes.SUCCESS
    ) {
      state.searchState.initLoading = false;
      state.searchState.fetchLoading = false;

      state.searchState.searchFinishedTime = new Date().getTime();
      state.searchState.mapLoading = false;

      if (state.currentMapBounds) {
        state.primaryMapBounds = state.currentMapBounds;
      }

      initSubscription?.unsubscribe();
      sseSubscription?.unsubscribe();
      fetchSubscription?.unsubscribe();

      state.searchState.state = payload.state;
    }
  },

  setSearchByType(state: State, type: Search.SearchByTypes) {
    state.searchState.searchBy = type;
  },

  saveSearchState(state: State, payload: Search.ISearchUIState) {
    state.searchState.route = payload.route;
    state.searchState.positionOnList = payload.position;
  },

  switchView(state: State, view: Search.ViewTypes) {
    state.searchState.view = view;
  },

  setSuggestLoading(state: State, payload: Search.ISearchState) {
    state.searchState.suggestState = payload.state;
    if (payload.state === Search.SearchStateTypes.STARTED)
      state.searchState.suggestLoading = true;
    if (
      payload.state === Search.SearchStateTypes.FAILED ||
      payload.state === Search.SearchStateTypes.SUCCESS
    ) {
      state.searchState.suggestLoading = false;
    }
  },

  updatePageMeta(state: State, pageMeta: PageMeta) {
    state.pageMeta = pageMeta;
  },

  setPrimaryMapBounds(state: State, bounds: mapboxGl.LngLatBounds) {
    state.primaryMapBounds = bounds;
  },

  setMapLoading(state: State, loading) {
    state.searchState.mapLoading = loading;
  },

  updateHotelsInBounds(state: State, bounds: any) {
    // if (state.results != null) {
    //   const hotels = state.results.listCollection.hotels.slice().filter(it => it.byMap && bounds.contains(it.coordinate.toMapBoxSystem())).map(it => it.byMap = false);
    // }
  },

  setRedoState(state: State, redoState: boolean) {
    state.showRedo = redoState;
  },

  setSearchStateByMap(state: State, finished: boolean) {
    state.searchFinishedInCurrentBounds = finished;
  },

  setRoomId(state: State, roomId: string) {
    state.roomId = roomId;
  },

  setCategorySelectionModal(
    state: State,
    {
      isShown,
      prices,
    }: { isShown: boolean; prices?: State['categorySelectionModal']['prices'] },
  ) {
    if (!isShown || !prices) {
      state.categorySelectionModal = {
        isShown: false,
        prices: {
          [Filter.StarsType.Three]: null,
          [Filter.StarsType.Four]: null,
          [Filter.StarsType.Five]: null,
        },
      };
      return;
    }

    state.categorySelectionModal = {
      isShown: true,
      prices: prices,
    };
  },

  // expiration
  setSearchExpiration(state: State, expirationAt: Date) {
    state.searchExpirationAt = expirationAt;
  },
  setSearchExpired(state: State, isExpired: boolean) {
    state.isSearchExpired = isExpired;
  },
};

const actions = <ActionTree<State, any>>{
  async search(
    store: ActionContext<State, any>,
    payload: {
      withoutReset: boolean;
      source?: string;
      forceMap?: boolean;
      forceClear?: boolean;
    },
  ) {
    await store.dispatch('preventAllSubscriptions');

    const withoutReset = payload ? payload.withoutReset : false;

    const currentRoomId = store.state.roomId;

    const initLoading = !withoutReset || store.state.hotels.length === 0;

    if (!withoutReset) {
      store.commit('reset');
      store.commit('setFilters', Filter.HotelCollectionFilterModel.compose());
    }

    const isSearchByMap =
      store.state.currentMapBounds &&
      (store.state.filters.useMap() || payload?.forceMap === true) &&
      payload.forceClear !== true;

    store.commit('updatePageMeta', {
      page: 0,
      from: 0,
    });

    store.commit('setSearchExpiration', null);
    store.commit('setSearchExpired', false);
    ExpirationCountdown.stop();
    store.dispatch(
      'support/setRefreshDialogShow',
      { show: false, messgage: null },
      { root: true },
    );

    const criteria: HotelSearchCriteria =
      store.rootState.searchCriteria.criteria;

    const { id, checkin, checkout, adults, hotelId, children } = criteria;

    store.dispatch('support/updateIntercomSettings', {}, { root: true });

    store.commit('updateSearchState', {
      state: Search.SearchStateTypes.STARTED,
      initLoading: initLoading,
      fetchLoading: true,
      mapLoading: isSearchByMap,
    });

    if (isSearchByMap) {
      store.commit('setSearchByType', Search.SearchByTypes.GEO);
    } else {
      store.commit('setSearchByType', Search.SearchByTypes.SUGGEST);
    }

    const composeCoordinates = (): {
      lat: number;
      lon: number;
    } => {
      try {
        const coordinates = criteria.coordinates;

        if (coordinates) {
          return coordinates;
        }

        return parseIdToCoordinates(id);
      } catch (error) {
        console.log(error);
        return null;
      }
    };

    const userCategory: User.UserCategory.Category =
      store.rootState.user.category;

    const requestCriteria: InitRequest = await store.dispatch('attachFilters', {
      checkin: checkin,
      checkout: checkout,
      guests: adults,
      user_country: 'GB',
      mode: isSearchByMap ? 1 : 0,
      pricing_mode:
        userCategory === User.UserCategory.Category.Individual ? 1 : 0,
    });

    if (hotelId) {
      requestCriteria.id = hotelId;
    }

    if (children.length > 0) {
      requestCriteria.children = children.join(',');
    }

    const idAsCoords = composeCoordinates();

    if (isSearchByMap) {
      const bounds = store.state.currentMapBounds;

      if (!bounds) {
        store.commit('updateSearchState', {
          state: Search.SearchStateTypes.FAILED,
        });

        return;
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      requestCriteria.lat1 = ne.lat;
      requestCriteria.lon1 = ne.lng;
      requestCriteria.lat2 = sw.lat;
      requestCriteria.lon2 = sw.lng;
    } else if (idAsCoords) {
      requestCriteria.lat = idAsCoords.lat;
      requestCriteria.lon = idAsCoords.lon;
    }

    const suggest = (await store.rootGetters['suggest/getSuggest'](
      Suggest.Ids.HOTEL,
    )) as Suggest.Suggest<Suggest.HotelSuggest>;

    Events.Hotel.Search.start(criteria, suggest?.source);

    initSubscription = HotelService.searchV2Init(
      requestCriteria,
      criteria.getNights(),
      currentRoomId,
    )
      .pipe(finalize(() => store.commit('setLoading', false)))
      .subscribe(
        res => {
          store.commit('setRoomId', res['room-id']);
          store.commit('setHotels', res.data.hotels);

          store.dispatch('sse');
        },
        err => {
          store.commit('updateSearchState', {
            state: Search.SearchStateTypes.FAILED,
            error: err,
          });

          store.dispatch('seachFinishEvent');
        },
      );
  },

  async attachFilters(
    store: ActionContext<State, any>,
    requestCriteria: InitRequest,
  ): Promise<InitRequest> {
    const filter = store.state.filters;

    if (filter.priceRange && filter.priceRange.left !== filter.priceRange.min) {
      requestCriteria.min_price = filter.priceRange.left / 100;
    }

    if (
      filter.priceRange &&
      filter.priceRange.right !== filter.priceRange.max
    ) {
      requestCriteria.max_price = filter.priceRange.right / 100;
    }

    const amenities = filter.facilities
      .filterSelected()
      .map(it => (it as Filter.Facility).id);

    if (amenities.length > 0) {
      requestCriteria.amenities = amenities.join(',');
    }

    const hotelTypes = filter.hotelTypes
      .filterSelected<Filter.HotelType>()
      .map(it => it.id);

    const rating = filter.rating.filter(
      it => it.selected && it.type !== Filter.RatingType.Any,
    );

    const stars = filter.stars.filter(it => it.selected);

    if (stars.length > 0) {
      requestCriteria.stars = stars.map(it => it.type);
    }

    if (rating.length > 0) {
      requestCriteria.min_rating = rating[0].type;
    }

    if (hotelTypes.length > 0) {
      requestCriteria.accommodation_types = hotelTypes.join(',');
    }

    const rewardPrograms = filter.rewardProgram
      .filterSelected()
      .map(it => (it as Filter.RewardProgram).id);

    if (rewardPrograms.length > 0) {
      requestCriteria.reward_programs = rewardPrograms.join(',');
    }

    const payLater = filter.options
      .filterSelected<Filter.Option>()
      .some(it => it.id === Filter.OptionType.PayLater);

    if (payLater) {
      requestCriteria.pay_later = filter.options
        .filterSelected<Filter.Option>()
        .some(it => it.id === Filter.OptionType.PayLater);
    }

    const payOnline = filter.options
      .filterSelected<Filter.Option>()
      .some(it => it.id === Filter.OptionType.PayNow);

    if (payOnline) {
      requestCriteria.pay_online = filter.options
        .filterSelected<Filter.Option>()
        .some(it => it.id === Filter.OptionType.PayNow);
    }

    const isGenius = filter.options
      .filterSelected<Filter.Option>()
      .some(it => it.id === Filter.OptionType.IsGenius);

    if (isGenius) {
      requestCriteria.is_genius = true;
    }

    const breakfast = filter.options
      .filterSelected<Filter.Option>()
      .some(it => it.id === Filter.OptionType.FreeBreakfast);

    if (breakfast) {
      requestCriteria.free_breakfast = true;
    }

    const freeCancellation = filter.options
      .filterSelected<Filter.Option>()
      .some(it => it.id === Filter.OptionType.FreeCancellation);

    if (freeCancellation) {
      requestCriteria.free_cancellation = true;
    }

    requestCriteria.sort = filter.sorting;

    return requestCriteria;
  },

  sse(store: ActionContext<State, any>) {
    sseSubscription?.unsubscribe();

    const roomId = store.state.roomId;

    sseSubscription = SSEHotels.observeSearchEvents(roomId).subscribe(
      res => {
        if (res.type === SSEHotels.Types.SearchDone) {
          sseSubscription?.unsubscribe();

          store.dispatch('fetch');
        }
      },
      err => {
        store.commit('updateSearchState', {
          state: Search.SearchStateTypes.FAILED,
          error: err,
        });

        store.dispatch('seachFinishEvent');
      },
    );
  },

  fetch(store: ActionContext<State, any>, { pagination = false } = {}) {
    fetchSubscription?.unsubscribe();

    const roomId = store.state.roomId;
    const pageMeta = store.state.pageMeta;

    store.commit('setFetchLoading', true);

    const requestCriteria: FetchRequest = {
      from: pageMeta.from,
      room_id: roomId,
    };

    const criteria: HotelSearchCriteria =
      store.rootState.searchCriteria.criteria;

    fetchSubscription = HotelService.searchV2Fetch(
      requestCriteria,
      criteria.getNights(),
    )
      .pipe(
        map(res => {
          if (
            store.state.hotels.length === 0 &&
            res.data.hotels.length === 0 &&
            store.state.filters.isDefault()
          ) {
            throw {
              header: 'NO_RESULTS',
            };
          }

          return res;
        }),
      )
      .subscribe(
        res => {
          store.commit('setHotels', res.data.hotels);

          const { from, expirationTime, total, totalProcessed } = res.data;
          const page = calculatePage(from);

          store.commit('updatePageMeta', {
            page,
            from,
            total,
            totalProcessed,
          });

          const expirationDatetime = new Date(expirationTime);
          const expirationAt = expirationDatetime.isValid()
            ? expirationDatetime
            : null;
          store.commit('setSearchExpiration', expirationAt);
          ExpirationCountdown.init(expirationAt, () =>
            store.commit('setSearchExpired', true),
          );

          store.commit('updateSearchState', {
            state: Search.SearchStateTypes.SUCCESS,
          });

          store.dispatch('fetchHotelsCategoryPrices');

          if (!pagination) {
            store.dispatch('seachFinishEvent');
          }
        },
        err => {
          store.commit('updateSearchState', {
            state: Search.SearchStateTypes.FAILED,
            error: err,
          });

          if (!pagination) {
            store.dispatch('seachFinishEvent');
          }
        },
      );
  },

  async seachFinishEvent(store: ActionContext<State, any>) {
    const criteria: HotelSearchCriteria =
      store.rootState.searchCriteria.criteria;

    const props = {
      booking_window: criteria.getBookingWindow(),
      stay_nights: criteria.getNights(),
      default_search: criteria.isDefaultDates(),
      checkin: criteria.checkin + '',
      checkout: criteria.checkout + '',
      duration: (
        new Date().getTime() - store.state.searchState.searchStartTime
      ).millisecondsToSec(),
      error: false,
    };

    const filters: InitRequest = await store.dispatch('attachFilters', {});

    props['filters'] = filters;

    // if (store.state.searchState.isFailed()) {
    //   props.error = true;
    //   props['error_header'] = store.state.searchState.error?.header;
    //   props['error_text'] = store.state.searchState.error?.message;
    // }

    if (store.state.hotels && store.state.roomId) {
      props['room_id'] = store.state.roomId;
    }

    const suggest = (await store.rootGetters['suggest/getSuggest'](
      Suggest.Ids.HOTEL,
    )) as Suggest.Suggest<Suggest.HotelSuggest>;

    if (suggest) {
      props['destination_type'] = suggest.source?.type;
      props['destination'] = suggest.title;

      if (suggest.locationType === Suggest.LocationType.HOTEL) {
        props['hotel_id'] = suggest.source?.id;
        props['hotel'] = suggest.source?.title;
      }

      const country = readQuery('country');
      const city = readQuery('city');
      const state = readQuery('state');

      if (country) {
        props['country'] = country;
      }

      if (city) {
        props['city'] = city;
      }

      if (state) {
        props['state'] = state;
      }
    }

    if (store.state.searchState.isSuccess()) {
      props['total_processed'] = store.state.pageMeta.totalProcessed;
      props['total_hotels'] = store.state.pageMeta.total;
    }

    Events.Hotel.Search.finish(props);
  },

  syncFilters(
    store: ActionContext<State, any>,
    payload: {
      syncWith: Filter.HotelCollectionFilterModel;
      filters: Filter.Query;
    },
  ): Filter.HotelCollectionFilterModel {
    const {
      stars,
      rating,
      priceRange,
      sorting,
      selectableFilters,
      currency,
      locale,
    } = payload.filters;

    const current = payload.syncWith;

    const currentSelectable = {
      options: current.options,
      facilities: current.facilities,
      hotelTypes: current.hotelTypes,
      rewardProgram: current.rewardProgram,
    };

    let { options, facilities, hotelTypes, rewardProgram } = selectableFilters
      ? selectableFilters.reduce((acc, it) => {
          const key = it.key;
          const items = it.items;
          acc[key] = items;
          return acc;
        }, currentSelectable)
      : currentSelectable;

    const currentPriceRange =
      current.priceRange ??
      Filter.HotelCollectionFilterModel.compose().priceRange;

    let newPriceRange = priceRange
      ? currentPriceRange.copyWith({
          left: priceRange.left,
          right: priceRange.right,
        })
      : currentPriceRange;

    if (
      store.state.filters.priceRange &&
      currency &&
      currency !== store.state.filters.priceRange.currency.currency
    ) {
      newPriceRange = Filter.HotelCollectionFilterModel.compose().priceRange;
    }

    if (locale) {
      options = options.map(it => it.relocalize());

      facilities = facilities.map(it => it.relocalize());

      hotelTypes = hotelTypes.map(it => it.relocalize());
    }

    return new Filter.HotelCollectionFilterModel({
      stars: stars !== undefined ? Filter.Stars.compose(stars) : current.stars,
      rating:
        rating !== undefined ? Filter.Rating.compose(rating) : current.rating,
      options,
      facilities,
      hotelTypes,
      rewardProgram,
      priceRange: newPriceRange,
      sorting: sorting ?? current.sorting,
    });
  },

  async applyFilter(
    store: ActionContext<State, any>,
    filters: Filter.Query,
  ): Promise<boolean> {
    const updated: Filter.HotelCollectionFilterModel = await store.dispatch(
      'syncFilters',
      {
        syncWith: Filter.HotelCollectionFilterModel.compose(),
        filters,
      },
    );

    let needSearch = false;

    const current = store.state.filters;

    const isChanged = !updated.equals(current);

    if (isChanged) {
      needSearch = true;
      store.commit('setFilters', updated);
    }

    store.commit('setFilters', updated);

    if (filters.bounds && (updated.useMap() || filters.forceMap)) {
      const bounds = new mapboxGl.LngLatBounds(
        filters.bounds.sw as mapboxGl.LngLatLike,
        filters.bounds.ne as mapboxGl.LngLatLike,
      );

      const swCurrent = store.state.currentMapBounds
        ?.getSouthWest()
        ?.toString();

      const neCurrent = store.state.currentMapBounds
        ?.getNorthEast()
        ?.toString();

      const sw = bounds.getSouthWest().toString();
      const ne = bounds.getNorthEast().toString();

      const isSame = sw === swCurrent && ne === neCurrent;

      if (!isSame) {
        needSearch = true;

        store.commit('setMapBounds', bounds);
      }
    }

    if (needSearch) {
      store.commit('updatePageMeta', {
        page: 0,
        from: 0,
        total: store.state.pageMeta.total,
        totalProcessed: store.state.pageMeta.totalProcessed,
      });
    }

    return needSearch;
  },

  async resetFilters(
    store: ActionContext<State, any>,
    withoutMap: boolean = false,
  ) {
    if (!withoutMap) {
      store.commit('setMapBounds', store.state.primaryMapBounds);
    }

    const onDefault = store.state.filters.isDefault();

    store.commit('setFilters', Filter.HotelCollectionFilterModel.compose());

    store.commit('updatePageMeta', {
      page: 0,
      from: 0,
      total: store.state.pageMeta.total,
      totalProcessed: store.state.pageMeta.totalProcessed,
    });

    if (onDefault) {
      return;
    }

    return;

    // await store.dispatch('search', {
    //   withoutReset: true,
    //   map: store.state.filters.useMap(),
    // });
  },

  async paginate(store: ActionContext<State, any>, direction: number) {
    // -1 left, 1 right

    const pageMeta = store.state.pageMeta;

    if (pageMeta.page === 0 && direction === -1) {
      return;
    }

    const hasNext = pageMeta.totalProcessed > pageMeta.from;

    if (!hasNext && direction === 1) {
      return;
    }

    const page = pageMeta.page + direction;
    const from = page * Search.limitPerPage;

    store.commit('updatePageMeta', {
      page,
      from,
      total: pageMeta.total,
      totalProcessed: pageMeta.totalProcessed,
    });

    store.dispatch('fetch', { pagination: true });
  },

  async preventAllSubscriptions(store: ActionContext<State, any>) {
    initSubscription?.unsubscribe();
    sseSubscription?.unsubscribe();
    fetchSubscription?.unsubscribe();
  },

  async fetchHotelsCategoryPrices(store: ActionContext<State, any>) {
    const testTool: Configurator.State = Configurator.builder();

    if (
      !store.getters.isDefaultStarsState ||
      testTool.hotelModule.starRatingSurveyDisplayFrequencies ===
        HotelModuleTest.StarRatingSurveyDisplayFrequencies.Disabled
    ) {
      return;
    }

    const stored = Search.HotelCategorySurvey.get();
    if (
      testTool.hotelModule.starRatingSurveyDisplayFrequencies ===
        HotelModuleTest.StarRatingSurveyDisplayFrequencies.Once &&
      stored?.isSurveyShowed
    ) {
      return;
    }

    const getAveragePriceByStar = (stars: number) => {
      const list = store.state.hotels
        ?.slice()
        ?.filter(hotel => hotel.stars === stars);
      return (
        list.reduce((a, b) => a + (b?.price?.effectivePrice ?? 0), 0) /
        list.length
      );
    };

    const prices = {
      [Filter.StarsType.Three]: getAveragePriceByStar(3),
      [Filter.StarsType.Four]: getAveragePriceByStar(4),
      [Filter.StarsType.Five]: getAveragePriceByStar(5),
    };

    store.commit('setCategorySelectionModal', {
      isShown: true,
      prices,
    });
  },

  async setHotelsCategory(
    store: ActionContext<State, any>,
    stars: Filter.StarsType,
  ) {
    const destination = store.rootState.searchCriteria.destination;
    const hotel_class = stars ? stars : Filter.StarsType.Any;
    Events.Hotel.Search.hotelCategorySelection({
      destination,
      hotel_class,
    });
    store.commit('setCategorySelectionModal', {
      isShown: false,
    });

    Search.HotelCategorySurvey.save([hotel_class], true);
  },

  checkExpiration(store: ActionContext<State, any>) {
    if (!store.state.searchExpirationAt) {
      store.commit('setSearchExpired', false);
      return;
    }

    ExpirationCountdown.resume(store.state.searchExpirationAt, () =>
      store.commit('setSearchExpired', true),
    );
  },
};

const calculatePage = (
  from: number,
  limit: number = Search.limitPerPage,
): number => {
  const adjustedFrom = Math.max(from, 0);

  const page = Math.floor(adjustedFrom / limit);

  return page;
};

export const search = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
